/* eslint-env es6, browser */
import React, { useEffect, useRef, useState, useCallback } from "react";
import "../styles/featuredbrands.css";
import { useDispatch, useSelector } from "react-redux";
import { fetchBrands } from "../actions/BrandActions";
import { useImage } from "../contexts/ImageContext";
import { useNavigate } from "react-router-dom";
import { FeaturedBrandsSkeleton } from "./HomeSkeletons";

function FeaturedBrands() {
  const dispatch = useDispatch();
  const { brands, loading: brandsLoading } = useSelector(
    (state) => state.brands,
  );
  const { getImageUrl } = useImage();
  const navigate = useNavigate();

  // State for drag functionality
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [momentum, setMomentum] = useState(0);

  // Refs
  const viewportRef = useRef(null);
  const animationRef = useRef(null);
  const lastScrollTime = useRef(0);
  const lastScrollLeft = useRef(0);

  useEffect(() => {
    dispatch(fetchBrands());
  }, [dispatch]);

  // Cleanup animation frame on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  const scrollByPage = useCallback((direction) => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const amount = viewport.clientWidth;
    viewport.scrollBy({ left: direction * amount, behavior: "smooth" });
  }, []);

  // Handle mouse/touch start
  const handleDragStart = useCallback((e) => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    setIsDragging(true);
    setStartX(e.type === "mousedown" ? e.pageX : e.touches[0].pageX);
    setScrollLeft(viewport.scrollLeft);

    // Cancel any ongoing momentum
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      setMomentum(0);
    }
  }, []);

  // Handle mouse/touch move
  const handleDragMove = useCallback(
    (e) => {
      if (!isDragging) return;

      e.preventDefault();
      const viewport = viewportRef.current;
      if (!viewport) return;

      const x = e.type === "mousemove" ? e.pageX : e.touches[0].pageX;
      const walk = (startX - x) * 1.5; // Scroll speed multiplier
      viewport.scrollLeft = scrollLeft + walk;
    },
    [isDragging, startX, scrollLeft]
  );

  // Handle mouse/touch end
  const handleDragEnd = useCallback(
    (e) => {
      if (!isDragging) return;

      const viewport = viewportRef.current;
      if (!viewport) return;

      setIsDragging(false);

      // Calculate momentum based on scroll speed
      const currentTime = Date.now();
      const timeDiff = currentTime - lastScrollTime.current;
      const scrollDiff = viewport.scrollLeft - lastScrollLeft.current;

      if (timeDiff > 0 && timeDiff < 100) {
        const velocity = scrollDiff / timeDiff;
        setMomentum(velocity * 15); // Momentum multiplier
      }

      lastScrollTime.current = currentTime;
      lastScrollLeft.current = viewport.scrollLeft;

      // Apply momentum
      if (Math.abs(momentum) > 0.1) {
        const applyMomentum = () => {
          viewport.scrollLeft += momentum;
          setMomentum(momentum * 0.95); // Decay factor

          if (Math.abs(momentum) > 0.1) {
            animationRef.current = requestAnimationFrame(applyMomentum);
          } else {
            setMomentum(0);
          }
        };
        animationRef.current = requestAnimationFrame(applyMomentum);
      }
    },
    [isDragging, momentum]
  );

  // Handle wheel scroll
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const viewport = viewportRef.current;
    if (!viewport) return;

    viewport.scrollLeft += e.deltaY;
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e) => {
      const viewport = viewportRef.current;
      if (!viewport) return;

      switch (e.key) {
        case "ArrowLeft":
          e.preventDefault();
          scrollByPage(-1);
          break;
        case "ArrowRight":
          e.preventDefault();
          scrollByPage(1);
          break;
        case "Home":
          e.preventDefault();
          viewport.scrollLeft = 0;
          break;
        case "End":
          e.preventDefault();
          viewport.scrollLeft = viewport.scrollWidth;
          break;
        default:
          break;
      }
    },
    [scrollByPage]
  );

  if (brandsLoading && (!brands || brands.length === 0)) {
    return <FeaturedBrandsSkeleton />;
  }

  return (
    <div className="featured-brands">
      <h2 className="title">Featured Brands</h2>
      <p className="description">
        haircare, makeup, skincare + fragrance for all.
      </p>

      <div className="brands-carousel">
        <button
          className="carousel-nav prev"
          aria-label="Previous brands"
          onClick={() => scrollByPage(-1)}
        >
          ‹
        </button>

        <div
          className="brands-viewport"
          ref={viewportRef}
          onMouseDown={handleDragStart}
          onMouseMove={handleDragMove}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
          onWheel={handleWheel}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          role="region"
          aria-label="Featured brands carousel"
          style={{ cursor: isDragging ? "grabbing" : "grab" }}
        >
          <div className="brands-track">
            {brands.map((brand) => (
              <div
                key={brand.brand_id || brand.name}
                className="brand-item"
                onClick={() => navigate(`/search?b=${brand.name}`)}
                style={{ pointerEvents: isDragging ? "none" : "auto" }}
              >
                <img
                  src={getImageUrl(brand.image)}
                  alt={brand.name}
                  className="brand-image"
                  draggable={false}
                />
                <p className="brand-name">{brand.name}</p>
              </div>
            ))}
          </div>
        </div>

        <button
          className="carousel-nav next"
          aria-label="Next brands"
          onClick={() => scrollByPage(1)}
        >
          ›
        </button>
      </div>
    </div>
  );
}

export default FeaturedBrands;
